import React, { Fragment, useState } from 'react';
import { compose } from 'recompose';
import { reduxForm, getFormValues, Field } from 'redux-form';
import { connect } from 'react-redux';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';

import {
    AddButton,
    CardElement,
    Modal,
    TextInput,
    SelectInput,
    SwitchInput,
    StatusChip,
    EmptyState,
    DeleteButton,
} from '../../_Components';
import { Options } from '../../_utils';
import { confirm } from '../../_utils/_helpers/confirm';

const { FieldsTypesOptions, OperatorOptions, CallCenterOptions, ResultOptions } = Options;

const COMBOBOX_TYPES = ['select', 'multiselect', 'radio'];

const SizeOptions = Array.from({ length: 12 }, (_, index) => ({
    id: index + 1,
    name: String(index + 1)
}));

const CallValue = [
    { id: 'value', name: 'Значение' },
    { id: 'label', name: 'Надпись' },
];

const TrasferValue = [
    { id: 'status', name: 'Статус' },
    { id: 'number', name: 'Номер' },
];

const RelatedValue = [
    { id: 'value', name: 'Значение' },
    { id: 'label', name: 'Надпись' },
];

const CYR_MAP = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
    й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
    у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
    э: 'e', ю: 'yu', я: 'ya',
};

const toKey = (value) => String(value || '')
    .toLowerCase()
    .split('')
    .map((char) => (CYR_MAP[char] !== undefined ? CYR_MAP[char] : char))
    .join('')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 30);

const typeName = (id) => {
    const item = FieldsTypesOptions.find((option) => option.id === id);
    return item ? item.name : (id || '');
};

const fieldWord = (count) => {
    const hundred = count % 100;
    const ten = count % 10;
    if (hundred > 10 && hundred < 20) return 'полей';
    if (ten === 1) return 'поле';
    if (ten >= 2 && ten <= 4) return 'поля';
    return 'полей';
};

const dictName = (comboboxes, id) => {
    if (!id) return '';
    const item = (comboboxes || []).find((option) => String(option.id) === String(id));
    return item ? item.name : '';
};

const SortHead = ({ id, sortKey, sortDir, onSort, children, style }) => (
    <th
        className={`can-sort${sortKey === id ? ' is-sort' : ''}`}
        style={style}
        onClick={() => onSort(id)}
    >
        {children}
        <span className="ar">{sortDir > 0 ? '↑' : '↓'}</span>
    </th>
);

const Edit = ({ formValues, fields = [], comboboxes = [], change, isNew, editIndex }) => {
    const type = formValues && formValues.type;
    const fieldsArray = (fields || []).map((field) => ({ id: field.value, name: field.label }));
    const cols = (formValues && formValues.cols) || [];
    const keyValue = ((formValues && formValues.value) || '').trim();
    const taken = (fields || []).some((field, index) => (
        !!(keyValue && (field.value || '') === keyValue && (isNew || index !== editIndex))
    ));

    const addCol = () => {
        if (!change) return;
        change('cols', [
            ...cols,
            {
                field: '',
                required: false,
                readonly: false,
                crm: false,
                callPhone: false,
                sizeX: 2,
                sizeY: 1,
                row: cols.length,
                col: 0,
                delete: true,
                edit: true
            }
        ]);
    };

    const removeCol = (colIndex) => {
        if (!change) return;
        change('cols', cols.filter((_, index) => index !== colIndex));
    };

    const onLabelChange = (event, next) => {
        if (isNew && change) {
            change('value', toKey(next));
        }
    };

    const onKeyChange = (event, next) => {
        if (change) {
            change('value', String(next || '').replace(/[^a-zA-Z0-9_]/g, '_'));
        }
    };

    return (
        <Fragment>
            <div className="pc-fgrid">
                <div className="pc-f--full">
                    <Field
                        name="label"
                        required={true}
                        component={TextInput}
                        label="Название"
                        placeholder="Как поле называется для оператора"
                        onChange={onLabelChange}
                    />
                </div>
                <div className="pc-f--full">
                    <Field
                        name="value"
                        required={true}
                        component={TextInput}
                        label="Ключ"
                        mono={true}
                        placeholder="latinskiy_klyuch"
                        onChange={onKeyChange}
                    />
                    <div className={`pc-field__hint${taken ? ' is-err' : ''}`}>
                        {taken
                            ? 'Такой ключ уже занят другим полем.'
                            : isNew
                                ? 'Подставится из названия — можно изменить.'
                                : 'Смена ключа разорвёт связь с уже собранными данными.'}
                    </div>
                </div>
                <div className={COMBOBOX_TYPES.indexOf(type) >= 0 ? '' : 'pc-f--full'}>
                    <Field
                        name="type"
                        required={true}
                        component={SelectInput}
                        options={FieldsTypesOptions}
                        label="Тип"
                    />
                </div>
                {COMBOBOX_TYPES.indexOf(type) >= 0 && (
                    <Field
                        name="combobox"
                        required={true}
                        component={SelectInput}
                        options={comboboxes}
                        label="Справочник"
                    />
                )}
                {type === 'related' && (
                    <Fragment>
                        <Field
                            name="related.field"
                            required={true}
                            component={SelectInput}
                            options={fieldsArray}
                            label="Поле"
                        />
                        <Field
                            name="related.value"
                            required={true}
                            component={SelectInput}
                            options={RelatedValue}
                            label="Значение"
                        />
                    </Fragment>
                )}
                {type === 'integration' && (
                    <Fragment>
                        <Field
                            name="api.param"
                            required={true}
                            component={TextInput}
                            label="Параметр"
                        />
                        <Field
                            name="api.method"
                            required={true}
                            component={TextInput}
                            label="Метод"
                        />
                        <Field
                            name="api.value"
                            required={true}
                            component={SelectInput}
                            options={RelatedValue}
                            label="Значение"
                        />
                    </Fragment>
                )}
                {type === 'callcenter' && (
                    <Field
                        name="callcenter"
                        required={true}
                        component={SelectInput}
                        options={CallCenterOptions}
                        label="Параметр"
                    />
                )}
                {type === 'operator' && (
                    <Field
                        name="operator"
                        required={true}
                        component={SelectInput}
                        options={OperatorOptions}
                        label="Параметр"
                    />
                )}
                {type === 'call' && (
                    <Fragment>
                        <Field
                            name="call.field"
                            required={true}
                            component={SelectInput}
                            options={ResultOptions}
                            label="Параметр"
                        />
                        <Field
                            name="call.value"
                            required={true}
                            component={SelectInput}
                            options={CallValue}
                            label="Значение"
                        />
                    </Fragment>
                )}
                {type === 'transfer' && (
                    <Field
                        name="transfer"
                        required={true}
                        component={SelectInput}
                        options={TrasferValue}
                        label="Параметр"
                    />
                )}
            </div>
            {type === 'array' && (
                <div style={{ marginTop: 16 }}>
                    <div className="pc-sect__t">Колонки таблицы</div>
                    {cols.map((col, colIndex) => (
                        <div className="pc-fgrid" key={col._id || colIndex} style={{ marginBottom: 10 }}>
                            <Field
                                name={`cols[${colIndex}].field`}
                                required={true}
                                component={SelectInput}
                                options={fieldsArray}
                                label="Поле"
                            />
                            <Field
                                name={`cols[${colIndex}].sizeX`}
                                component={SelectInput}
                                options={SizeOptions}
                                label="Ширина"
                            />
                            <Field
                                name={`cols[${colIndex}].required`}
                                component={SwitchInput}
                                label="Обязательное"
                            />
                            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                                <Button color="secondary" size="small" onClick={() => removeCol(colIndex)}>
                                    Удалить
                                </Button>
                            </div>
                        </div>
                    ))}
                    <AddButton handleAddClick={addCol} label="Добавить колонку" />
                </div>
            )}
            <div className="pc-togs">
                <div className="pc-tog">
                    <Field name="require" component={SwitchInput} />
                    <span className="pc-tog__txt">
                        <b>Обязательное</b>
                        <span>Без него карточку не закрыть</span>
                    </span>
                </div>
                <div className="pc-tog">
                    <Field name="service" component={SwitchInput} />
                    <span className="pc-tog__txt">
                        <b>Служебное</b>
                        <span>Оператор поле не видит</span>
                    </span>
                </div>
            </div>
            <div className="pc-sect" style={{ paddingLeft: 0, paddingRight: 0 }}>
                <div className="pc-sect__t">Внешняя интеграция</div>
                <div className="pc-fgrid">
                    <div className="pc-f--full">
                        <Field
                            name="external.label"
                            component={TextInput}
                            label="Название в CRM"
                            placeholder="необязательно"
                        />
                    </div>
                    <Field
                        name="external.module"
                        component={TextInput}
                        label="Модуль"
                    />
                    <Field
                        name="external.field"
                        component={TextInput}
                        label="Поле"
                        mono={true}
                        placeholder="UF_CRM_…"
                    />
                </div>
            </div>
        </Fragment>
    );
};

const FieldsFunc = ({ formValues, change }) => {
    const [open, setOpen] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [query, setQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [sortKey, setSortKey] = useState('');
    const [sortDir, setSortDir] = useState(1);

    if (!formValues) {
        return null;
    }

    const fields = formValues.fields || [];
    const combobox = formValues.combobox || [];
    const comboboxes = combobox.map((item) => ({ id: item._id, name: item.name }));
    const q = query.trim().toLowerCase();

    const dups = {};
    fields.forEach((field) => {
        const key = (field.value || '').trim();
        if (key) dups[key] = (dups[key] || 0) + 1;
    });
    const dupKeys = new Set(Object.keys(dups).filter((key) => dups[key] > 1));

    const visible = fields
        .map((field, index) => ({ field, index }))
        .filter(({ field }) => {
            if (typeFilter && field.type !== typeFilter) return false;
            if (q && !`${field.label || ''} ${field.value || ''}`.toLowerCase().includes(q)) return false;
            return true;
        });

    const rows = sortKey
        ? [...visible].sort((a, b) => {
            const left = sortKey === 'type' ? typeName(a.field.type) : (a.field[sortKey] || '');
            const right = sortKey === 'type' ? typeName(b.field.type) : (b.field[sortKey] || '');
            return String(left).localeCompare(String(right), 'ru') * sortDir;
        })
        : visible;

    const onSort = (key) => {
        if (sortKey === key) {
            setSortDir((dir) => -dir);
            return;
        }
        setSortKey(key);
        setSortDir(1);
    };

    const openCreate = () => {
        setEditIndex(null);
        setOpen(true);
    };

    const openEdit = (index) => {
        setEditIndex(index);
        setOpen(true);
    };

    const closeEdit = () => {
        setOpen(false);
        setEditIndex(null);
    };

    const editing = editIndex != null ? fields[editIndex] : null;
    const initial = editing
        ? { ...editing }
        : { type: 'input', require: false, service: false };

    const save = (form) => {
        if (!form) return;
        const next = [...(formValues.fields || [])];
        if (editIndex != null && next[editIndex]) {
            next[editIndex] = { ...next[editIndex], ...form };
        } else {
            next.push(form);
        }
        change('fields', next);
        closeEdit();
    };

    const removeField = () => {
        if (editIndex == null) return;
        const field = fields[editIndex];
        confirm({
            title: 'Удалить поле?',
            message: `Поле «${field && field.label ? field.label : 'без названия'}» будет удалено. Собранные значения останутся в истории звонков.`,
            confirmLabel: 'Удалить',
            onConfirm: () => {
                change('fields', fields.filter((_, index) => index !== editIndex));
                closeEdit();
            }
        });
    };

    const saveDisabled = (values) => {
        if (!values) return true;
        const label = (values.label || '').trim();
        const key = (values.value || '').trim();
        if (!label || !key) return true;
        const taken = fields.some((field, index) => (
            (field.value || '') === key && (editIndex == null || index !== editIndex)
        ));
        if (taken) return true;
        if (COMBOBOX_TYPES.indexOf(values.type) >= 0 && !values.combobox) return true;
        return false;
    };

    return (
        <Fragment>
            <div className="pc-fill">
                <CardElement
                    fill
                    content={
                        <div className="pc-panel-body">
                            <div className="pc-toolbar">
                                <TextField
                                    className="pc-search pc-search--wide"
                                    size="small"
                                    placeholder="Поиск по названию или ключу"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 16, color: 'var(--muted-2)' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    select
                                    size="small"
                                    value={typeFilter}
                                    onChange={(event) => setTypeFilter(event.target.value)}
                                    style={{ width: 170 }}
                                >
                                    <MenuItem value="">Все типы</MenuItem>
                                    {FieldsTypesOptions.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                                    ))}
                                </TextField>
                                <span className="pc-rescount">
                                    {rows.length === fields.length
                                        ? `${fields.length} ${fieldWord(fields.length)}`
                                        : `${rows.length} из ${fields.length}`}
                                </span>
                                <AddButton
                                    label="Новое поле"
                                    handleAddClick={openCreate}
                                    variant="contained"
                                    style={{ marginLeft: 'auto' }}
                                />
                            </div>
                            <div className="pc-tblwrap">
                                <table className="pc-cards-tbl">
                                    <thead>
                                        <tr>
                                            <SortHead id="label" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                                                Название
                                            </SortHead>
                                            <SortHead id="value" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 220 }}>
                                                Ключ
                                            </SortHead>
                                            <SortHead id="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} style={{ width: 230 }}>
                                                Тип
                                            </SortHead>
                                            <th className="flags" style={{ width: 180, cursor: 'default' }}>Флаги</th>
                                            <th style={{ width: 44 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(({ field, index }) => (
                                            <tr
                                                key={field._id || field.value || index}
                                                className={field.deleted ? 'is-off' : ''}
                                                onClick={() => openEdit(index)}
                                            >
                                                <td>{field.label}</td>
                                                <td>
                                                    <span
                                                        className={`key${dupKeys.has((field.value || '').trim()) ? ' dup' : ''}`}
                                                        title={dupKeys.has((field.value || '').trim()) ? 'Ключ повторяется' : ''}
                                                    >
                                                        {field.value}
                                                    </span>
                                                </td>
                                                <td>
                                                    <StatusChip>{typeName(field.type)}</StatusChip>
                                                    {COMBOBOX_TYPES.indexOf(field.type) >= 0 && dictName(comboboxes, field.combobox) ? (
                                                        <span className="src">{dictName(comboboxes, field.combobox)}</span>
                                                    ) : null}
                                                </td>
                                                <td className="flags-cell">
                                                    <div className="flags">
                                                        {field.require ? <StatusChip tone="danger">обязательное</StatusChip> : null}
                                                        {field.service ? <StatusChip>служебное</StatusChip> : null}
                                                    </div>
                                                </td>
                                                <td className="act">
                                                    <IconButton
                                                        size="small"
                                                        tabIndex={-1}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openEdit(index);
                                                        }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {rows.length === 0 && (
                                    <EmptyState
                                        title="Ничего не найдено"
                                        description="Измените запрос или сбросьте фильтр по типу."
                                    />
                                )}
                            </div>
                        </div>
                    }
                />
            </div>
            {open && (
                <Modal
                    key={editIndex == null ? 'new' : editIndex}
                    width={560}
                    title={
                        <Fragment>
                            {editing ? (editing.label || 'Поле') : 'Новое поле'}
                            {editing && editing.value ? (
                                <StatusChip tone="mono">{editing.value}</StatusChip>
                            ) : null}
                        </Fragment>
                    }
                    open={open}
                    handleSaveClick={save}
                    handleCloseClick={closeEdit}
                    fields={<Edit comboboxes={comboboxes} fields={fields} isNew={editIndex == null} editIndex={editIndex} />}
                    initial={initial}
                    saveDisabled={saveDisabled}
                    extraActions={editIndex != null ? (
                        <DeleteButton handleDeleteClick={removeField} />
                    ) : null}
                />
            )}
        </Fragment>
    );
};

const mapStateToProps = (state) => {
    const formValues = getFormValues('project')(state);
    return { formValues };
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'project',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    }),
);

export default enhance(FieldsFunc);
